import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/questions";
import { COMPANY_INFO } from "@/lib/company";
import api from "@/lib/api";

/* -------------------------------------------------------------------------
   LOGIN / LANDING PAGE SPLIT-SCREEN
   - SINISTRA (60%): vetrina pubblica del catalogo TOP-TEC 2026 (8 categorie).
   - DESTRA (40%): form di login + CTA "Richiedi credenziali".
   - Mobile: login prima, catalogo accessibile via toggle "Vedi catalogo".
   ------------------------------------------------------------------------- */

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [mobileView, setMobileView] = useState("login"); // "login" | "catalog"
  const [lockedProduct, setLockedProduct] = useState(null); // {id, name, category}
  const [schedaModalOpen, setSchedaModalOpen] = useState(false);
  const loginFormRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/catalog");
        setCatalog(data || []);
        const first = (data || []).find((c) => c.count > 0);
        if (first) setActiveCat(first.slug);
      } catch {
        // Silente — la vetrina è un extra, non bloccare la login
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Accesso effettuato");
      const from = location.state?.from;
      if (data.role === "admin") {
        navigate(from && from.startsWith("/admin") ? from : "/admin");
      } else {
        navigate(from && !from.startsWith("/admin") ? from : "/");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product, categoryName) => {
    setLockedProduct({ id: product.id, name: product.name, category: categoryName });
    setMobileView("login");
    // sposta il focus sul form su desktop
    setTimeout(() => {
      loginFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      emailInputRef.current?.focus();
    }, 50);
  };

  const activeCategory = catalog.find((c) => c.slug === activeCat) || null;

  return (
    <div className="min-h-screen tm-grid-bg flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/70 backdrop-blur-sm shrink-0">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-3 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex items-center gap-6 md:gap-8">
            <img src={LOGO_URL} alt="TOP-TEC" className="h-10 md:h-14 tm-logo" data-testid="login-header-logo" />
            <div className="hidden md:block">
              <div className="font-heading text-lg text-white tracking-tighter" data-testid="company-legal">
                {COMPANY_INFO.legal}
              </div>
              <div className="text-slate-400 text-xs font-mono" data-testid="company-address">
                {COMPANY_INFO.address}
              </div>
            </div>
          </div>
          <div className="text-xs font-mono text-slate-400 flex flex-wrap gap-x-4 gap-y-0.5 md:block md:text-right md:space-y-0.5">
            <div>
              <span className="text-slate-500">tel.</span>{" "}
              <a href={`tel:${COMPANY_INFO.phoneTel}`} className="hover:text-orange-400 transition-colors" data-testid="company-phone">
                {COMPANY_INFO.phone}
              </a>
            </div>
            <div>
              <span className="text-slate-500">email</span>{" "}
              <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-orange-400 transition-colors" data-testid="company-email">
                {COMPANY_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile toggle */}
      <div className="md:hidden border-b border-slate-800 bg-slate-950/60 px-4 py-2 flex gap-2 shrink-0">
        <button
          type="button"
          onClick={() => setMobileView("login")}
          className={`flex-1 py-2 font-mono text-xs uppercase tracking-widest transition-colors border ${
            mobileView === "login"
              ? "border-orange-500 bg-orange-500/15 text-orange-300"
              : "border-slate-700 text-slate-400 hover:text-slate-200"
          }`}
          data-testid="mobile-tab-login"
        >
          Accedi
        </button>
        <button
          type="button"
          onClick={() => setMobileView("catalog")}
          className={`flex-1 py-2 font-mono text-xs uppercase tracking-widest transition-colors border ${
            mobileView === "catalog"
              ? "border-orange-500 bg-orange-500/15 text-orange-300"
              : "border-slate-700 text-slate-400 hover:text-slate-200"
          }`}
          data-testid="mobile-tab-catalog"
        >
          Catalogo 2026
        </button>
      </div>

      {/* Split layout */}
      <div className="flex-1 flex flex-col md:flex-row max-w-[1600px] w-full mx-auto">
        {/* COLONNA SINISTRA — Vetrina catalogo (60%) */}
        <section
          className={`md:w-[60%] md:border-r border-slate-800 p-4 md:p-8 ${
            mobileView === "catalog" ? "block" : "hidden md:block"
          }`}
          data-testid="public-catalog-section"
        >
          <div className="mb-6">
            <div className="tm-tag inline-block mb-3">Catalogo TOP-TEC 2026</div>
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tighter leading-tight mb-2">
              8 famiglie di prodotti industriali
            </h2>
            <p className="text-slate-400 text-sm max-w-xl">
              Esplora un'anteprima pubblica del catalogo. Per visualizzare codici d'ordine, formati e schede
              tecniche complete,{" "}
              <button
                type="button"
                data-testid="intro-accedi-link"
                onClick={() => emailInputRef.current?.focus()}
                className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
              >
                accedi
              </button>{" "}
              o{" "}
              <button
                type="button"
                data-testid="intro-request-link"
                onClick={() => setShowAccessModal(true)}
                className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
              >
                richiedi le credenziali
              </button>
              .
            </p>
          </div>

          {/* Griglia 8 categorie */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-6" data-testid="categories-grid">
            {catalog.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => cat.count > 0 && setActiveCat(cat.slug)}
                disabled={cat.count === 0}
                className={`relative text-left p-3 border transition-all group ${
                  activeCat === cat.slug
                    ? "border-white"
                    : "border-slate-800 hover:border-slate-600"
                } ${cat.count === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                style={{
                  backgroundColor: activeCat === cat.slug ? cat.color : "transparent",
                }}
                data-testid={`category-card-${cat.slug}`}
              >
                <div
                  className="w-full h-1 mb-2"
                  style={{ backgroundColor: cat.color }}
                />
                <div
                  className={`font-heading text-sm leading-tight tracking-tight ${
                    activeCat === cat.slug ? "text-white" : "text-slate-200"
                  }`}
                >
                  {cat.name}
                </div>
                <div
                  className={`font-mono text-[10px] uppercase tracking-widest mt-1 ${
                    activeCat === cat.slug ? "text-white/80" : "text-slate-500"
                  }`}
                >
                  {cat.count > 0 ? `${cat.count} prodotti` : "Prossimamente"}
                </div>
              </button>
            ))}
          </div>

          {/* Prodotti della categoria attiva */}
          {activeCategory && (
            <div data-testid="active-category-products">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 shrink-0"
                  style={{ backgroundColor: activeCategory.color }}
                />
                <div>
                  <h3 className="font-heading text-xl text-white tracking-tighter">
                    {activeCategory.name}
                  </h3>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {activeCategory.count} prodotti · anteprima pubblica
                  </div>
                </div>
              </div>

              {activeCategory.count === 0 ? (
                <div className="tm-card p-6 text-center text-slate-400 text-sm">
                  Questa famiglia sarà disponibile a breve nel Product Finder.
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {activeCategory.products.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleProductClick(p, activeCategory.name)}
                      className="tm-card tm-card-hover p-3 text-left group relative overflow-hidden"
                      data-testid={`public-product-card-${p.id}`}
                    >
                      <div className="aspect-square bg-slate-800/60 border border-slate-700 mb-3 overflow-hidden flex items-center justify-center">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <div className="text-slate-600 font-mono text-xs">TOP-TEC</div>
                        )}
                      </div>
                      <div className="font-heading text-sm text-white tracking-tight leading-tight line-clamp-2 mb-1">
                        {p.name}
                      </div>
                      <div className="text-slate-400 text-xs leading-snug line-clamp-3">
                        {p.brief_description || "—"}
                      </div>
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-orange-400/80 opacity-0 group-hover:opacity-100 transition-opacity">
                        Accedi per i dettagli →
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* COLONNA DESTRA — Login (40%) sticky su desktop */}
        <section
          className={`md:w-[40%] p-4 md:p-8 md:sticky md:top-0 md:self-start md:max-h-screen md:overflow-y-auto flex items-start justify-center ${
            mobileView === "login" ? "block" : "hidden md:flex"
          }`}
          ref={loginFormRef}
          data-testid="login-section"
        >
          <div className="w-full max-w-md">
            <div className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                Product Finder — Accesso riservato
              </div>
            </div>

            {/* Avviso prodotto bloccato */}
            {lockedProduct && (
              <div
                className="mb-4 border border-orange-500 bg-orange-500/10 p-4"
                data-testid="locked-product-notice"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-orange-400 mb-1">
                      🔒 Dettagli riservati
                    </div>
                    <div className="font-heading text-sm text-white tracking-tight leading-snug mb-1">
                      {lockedProduct.name}
                    </div>
                    <p className="text-slate-300 text-xs leading-relaxed">
                      Accedi o richiedi le credenziali per vedere codici d'ordine, formati e scheda tecnica
                      completa di questo prodotto <span className="text-orange-300">{lockedProduct.category}</span>.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLockedProduct(null)}
                    className="text-slate-500 hover:text-slate-300 shrink-0"
                    aria-label="Chiudi avviso"
                  >
                    ✕
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setSchedaModalOpen(true)}
                  className="mt-3 w-full py-2.5 px-3 border border-orange-500 bg-orange-500 hover:bg-orange-400 text-slate-900 font-mono text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                  data-testid="request-scheda-btn"
                >
                  📄 Richiedi scheda tecnica
                </button>
              </div>
            )}

            <div className="tm-card p-6 md:p-8">
              <div className="tm-tag inline-block mb-4">Login</div>
              <h1 className="font-heading text-2xl md:text-3xl text-white tracking-tighter mb-2" data-testid="login-title">
                Accedi alla piattaforma
              </h1>
              <p className="text-slate-400 text-sm mb-6">
                Inserisci le credenziali fornite. Admin e clienti utilizzano lo stesso form: il sistema
                riconosce il ruolo.
              </p>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="tm-label">Email</label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    required
                    className="tm-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label className="tm-label">Password</label>
                  <input
                    type="password"
                    required
                    className="tm-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="tm-btn-primary w-full disabled:opacity-50"
                  data-testid="login-submit"
                >
                  {loading ? "Accesso in corso..." : "Accedi"}
                </button>
              </form>

              {/* CTA richiesta credenziali */}
              <div className="mt-6 pt-6 border-t border-slate-800">
                <p className="text-xs text-slate-400 mb-3 font-mono uppercase tracking-widest">
                  Non hai ancora un account?
                </p>
                <button
                  type="button"
                  onClick={() => setShowAccessModal(true)}
                  className="w-full py-3 px-4 border-2 border-orange-500 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 font-mono uppercase tracking-widest text-sm transition-all group"
                  data-testid="request-access-btn"
                >
                  <span className="inline-flex items-center gap-2">
                    🔑 Richiedi credenziali di accesso
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                </button>
              </div>
            </div>

            <p className="text-center mt-6 text-xs text-slate-500 font-mono uppercase tracking-widest">
              © 2026 {COMPANY_INFO.brand} · Product Finder B2B
            </p>
          </div>
        </section>
      </div>

      {showAccessModal && (
        <AccessRequestModal onClose={() => setShowAccessModal(false)} />
      )}

      {schedaModalOpen && lockedProduct && (
        <SchedaRequestModal
          product={lockedProduct}
          onClose={() => setSchedaModalOpen(false)}
        />
      )}
    </div>
  );
}

function AccessRequestModal({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [saving, setSaving] = useState(false);
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await api.post("/access-requests", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      toast.success("Richiesta inviata! Ti contatteremo a breve via email.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tm-modal fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="access-modal">
      <div className="tm-card p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="tm-tag inline-block mb-4">Richiesta accesso</div>
        <h2 className="font-heading text-2xl text-white tracking-tighter mb-2">
          Richiedi le tue credenziali
        </h2>
        <p className="text-slate-400 text-sm mb-6">
          Compila il form: riceverai email e password personali direttamente da <span className="text-orange-400">{COMPANY_INFO.email}</span>.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tm-label">Nome e cognome *</label>
            <input type="text" required className="tm-input" value={form.name} onChange={setField("name")} data-testid="access-name" />
          </div>
          <div>
            <label className="tm-label">Email *</label>
            <input type="email" required className="tm-input" value={form.email} onChange={setField("email")} data-testid="access-email" />
          </div>
          <div>
            <label className="tm-label">Azienda</label>
            <input type="text" className="tm-input" value={form.company} onChange={setField("company")} data-testid="access-company" />
          </div>
          <div>
            <label className="tm-label">Telefono</label>
            <input type="tel" className="tm-input" value={form.phone} onChange={setField("phone")} data-testid="access-phone" />
          </div>
          <div>
            <label className="tm-label">Note (facoltativo)</label>
            <textarea rows={3} className="tm-input" value={form.message} onChange={setField("message")} placeholder="Es. settore di attività, prodotti di interesse…" data-testid="access-message" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="access-cancel">
              Annulla
            </button>
            <button type="submit" disabled={saving} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="access-submit">
              {saving ? "Invio..." : "Invia richiesta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SchedaRequestModal({ product, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [saving, setSaving] = useState(false);
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("L'email è obbligatoria");
      return;
    }
    setSaving(true);
    try {
      await api.post("/public/scheda-requests", {
        product_id: product.id,
        client_email: form.email.trim(),
        client_name: form.name.trim() || null,
        message: form.message.trim() || null,
      });
      toast.success(`Richiesta inviata! Riceverai la scheda tecnica di "${product.name}" via email.`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tm-modal fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="scheda-modal" onClick={onClose}>
      <div className="tm-card p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="tm-tag inline-block mb-4">Scheda tecnica</div>
        <h2 className="font-heading text-2xl text-white tracking-tighter mb-1">
          Richiedi la scheda tecnica
        </h2>
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-orange-400 mb-1">
            Prodotto
          </div>
          <div className="font-heading text-base text-white tracking-tight leading-tight" data-testid="scheda-product-name">
            {product.name}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
            Categoria · {(product.category || "").toString()}
          </div>
        </div>
        <p className="text-slate-400 text-sm mb-6">
          Lascia i tuoi contatti: ti invieremo via email la scheda tecnica completa con codici d'ordine, formati e dati tecnici.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tm-label">Nome e cognome</label>
            <input type="text" className="tm-input" value={form.name} onChange={setField("name")} data-testid="scheda-name" />
          </div>
          <div>
            <label className="tm-label">Email *</label>
            <input type="email" required className="tm-input" value={form.email} onChange={setField("email")} data-testid="scheda-email" />
          </div>
          <div>
            <label className="tm-label">Messaggio (facoltativo)</label>
            <textarea rows={3} className="tm-input" value={form.message} onChange={setField("message")} placeholder="Es. quantità richieste, applicazione specifica, tempistiche…" data-testid="scheda-message" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="scheda-cancel">
              Annulla
            </button>
            <button type="submit" disabled={saving} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="scheda-submit">
              {saving ? "Invio..." : "Invia richiesta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

